const features = [
  {
    icon: 'ri-shield-keyhole-line',
    title: 'Military-Grade Encryption',
    description:
      'All user data is protected with AES-256 encryption, the same standard used by governments and financial institutions worldwide.',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    image:
      'https://public.readdy.ai/ai/img_res/c6e06163c9defd7355288396d5a7f337.jpg',
    large: true,
  },
  {
    icon: 'ri-speed-up-line',
    title: 'Blazing Fast Performance',
    description:
      'Optimized for iOS hardware acceleration, our apps run smoothly even on older devices without draining your battery.',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    image:
      'https://readdy.ai/api/search-image?query=abstract%20speed%20performance%20concept%20visualization%2C%20warm%20amber%20and%20orange%20light%20streaks%20on%20clean%20white%20background%2C%20dynamic%20motion%20blur%20effect%2C%20technology%20concept%2C%20premium%20minimal%20design%2C%20no%20people%20no%20text&width=600&height=300&seq=feat002&orientation=landscape',
    large: false,
  },
  {
    icon: 'ri-user-settings-line',
    title: 'Privacy First Architecture',
    description:
      'Zero-log policy and on-device processing ensure your personal data never leaves your iPhone.',
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    image:
      'https://readdy.ai/api/search-image?query=abstract%20privacy%20data%20protection%20concept%2C%20soft%20rose%20pink%20and%20white%20gradient%20background%2C%20minimal%20lock%20icon%20visualization%2C%20clean%20modern%20design%2C%20premium%20tech%20illustration%2C%20no%20text%20no%20people&width=600&height=300&seq=feat003&orientation=landscape',
    large: false,
  },
];

const featurePoints = [
  { icon: 'ri-notification-off-line', title: 'Ad-Free Experience', desc: 'No interruptions. Pure, clean app interfaces.' },
  { icon: 'ri-global-line', title: 'Global Server Network', desc: 'Connect through 60+ locations worldwide.' },
  { icon: 'ri-contrast-2-line', title: 'Dark & Light Modes', desc: 'Full native iOS appearance support.' },
  { icon: 'ri-refresh-line', title: 'Auto Updates', desc: 'Always running the latest, most secure version.' },
  { icon: 'ri-customer-service-2-line', title: 'Dedicated Support', desc: 'Expert help whenever you need it.' },
  { icon: 'ri-battery-2-charge-line', title: 'Battery Optimized', desc: 'Minimal impact on your device battery life.' },
];

export default function FeaturesSection() {
  return (
    <section id="features" className="py-28 md:py-36 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-amber-500 text-xs font-semibold uppercase tracking-[0.3em] mb-4">
            / Why Choose Us
          </p>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
            Powerful Features,<br />
            <span className="text-amber-500">Built for You</span>
          </h2>
        </div>

        {/* Asymmetric Feature Grid */}
        <div className="flex flex-col lg:flex-row gap-6 mb-16">
          {/* Large left card */}
          <div className="lg:w-1/2 rounded-2xl overflow-hidden bg-white relative group" style={{ border: '1px solid #EEEBE6' }}>
            <div className="h-[240px] overflow-hidden">
              <img
                src={features[0].image}
                alt={features[0].title}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-8">
              <div className={`w-11 h-11 flex items-center justify-center rounded-xl mb-4 ${features[0].bg}`}>
                <i className={`${features[0].icon} text-xl ${features[0].color}`}></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{features[0].title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{features[0].description}</p>
            </div>
          </div>

          {/* Right two stacked cards */}
          <div className="lg:w-1/2 flex flex-col gap-6">
            {[features[1], features[2]].map((feat) => (
              <div
                key={feat.title}
                className="rounded-2xl overflow-hidden bg-white flex flex-col group flex-1"
                style={{ border: '1px solid #EEEBE6' }}
              >
                <div className="h-[160px] overflow-hidden">
                  <img
                    src={feat.image}
                    alt={feat.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex-1">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-3 ${feat.bg}`}>
                    <i className={`${feat.icon} text-lg ${feat.color}`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{feat.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{feat.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Feature Points Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featurePoints.map((fp) => (
            <div key={fp.title} className="flex items-start gap-4 p-6 bg-white rounded-xl" style={{ border: '1px solid #EEEBE6' }}>
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-50 flex-shrink-0">
                <i className={`${fp.icon} text-amber-500 text-lg`}></i>
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm mb-1">{fp.title}</p>
                <p className="text-gray-400 text-xs leading-relaxed">{fp.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
